// 成員：移出（IAM-08；SPEC #org-model-membership）。權限來源一覽改成成員抽屜（MemberDrawer.tsx，IAM-07）。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { orgApi, type MyOrg, type OrgMember } from '../../lib/planeApi';
import { Dialog } from '../../components/Dialog';
import { errText } from '../settings/OrgSection';
import { memberName } from './MembersTab';

/**
 * 移出成員（IAM-08，SPEC #org-model-membership）：DELETE /v1/orgs/{org}/members/{user}。只收回這個組織給的授權（他的個人組織、其他組織、
 * 別人分享給他的都不動）；他是 team 組織某些家戶的管理人 → 409 OWNS_HOMES（detail.homes），先交接，確認沒有接任者才設為無人管理。
 */
export function RemoveDialog({ org, member, close }: { org: MyOrg; member: OrgMember | null; close(): void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [ownsHomes, setOwnsHomes] = useState<{ home_id: string; display_name?: string }[] | null>(null);
  const [unassign, setUnassign] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  function reset() {
    setOwnsHomes(null);
    setUnassign(false);
    setErr(null);
    setResult(null);
    close();
  }
  const remove = useMutation({
    mutationFn: () => orgApi.removeMember(org, member!.user_id, unassign),
    onSuccess: (r) => {
      const x = (r?.result ?? {}) as { revoked_grants?: number; removed_home_memberships?: number; unassigned_homes?: unknown[] };
      setResult(t('people.left_done', { grants: x.revoked_grants ?? 0, homes: x.removed_home_memberships ?? 0, unassigned: x.unassigned_homes?.length ?? 0 }));
      void qc.invalidateQueries({ queryKey: ['org-members', org.org_id] });
      void qc.invalidateQueries({ queryKey: ['org-grants', org.org_id] });
      void qc.invalidateQueries({ queryKey: ['org-homes', org.org_id] });
    },
    onError: (e) => {
      if (isKinioError(e) && e.code === 'OWNS_HOMES') {
        const homes = (e.detail?.homes ?? []) as { home_id: string; display_name?: string }[];
        setOwnsHomes(Array.isArray(homes) ? homes : []);
        return;
      }
      setErr(errText(t, e));
    },
  });
  return (
    <Dialog open={!!member} title={t('people.remove_of', { name: memberName(member ?? undefined) })} onClose={reset}>
      {result ? (
        <div className="form">
          <p className="notice">{result}</p>
          <button className="btn btn-primary" onClick={reset}>
            {t('common.done')}
          </button>
        </div>
      ) : (
        <div className="form">
          <p className="muted">{t('people.remove_body')}</p>
          {ownsHomes && (
            <div className="notice">
              <p>{t('people.owns_homes')}</p>
              <ul className="plain-list">
                {ownsHomes.map((h) => (
                  <li key={h.home_id}>{h.display_name || h.home_id}</li>
                ))}
              </ul>
              <label className="check">
                <input type="checkbox" checked={unassign} onChange={(e) => setUnassign(e.target.checked)} />
                <span>{t('people.unassign_owned')}</span>
              </label>
            </div>
          )}
          {err && <div className="error">{err}</div>}
          <div className="row-end">
            <button className="btn" onClick={reset}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" disabled={remove.isPending || (!!ownsHomes && !unassign)} onClick={() => remove.mutate()}>
              {t('people.remove')}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
